import React, { useEffect, useRef, useState } from 'react';
import CameraInfoCard from './components/CameraInfoCard';
import CameraController from './components/CameraController';

interface DeviceCapabilities {
  resolutions?: Array<{ width: number; height: number }>;
  frameRates?: number[];
  codecs?: string[];
  audioSupport?: boolean;
  motionDetection?: boolean;
  panTiltZoom?: boolean;
  nightVision?: boolean;
  infrared?: boolean;
}

interface DeviceNetworkInfo {
  macAddress?: string;
  networkInterface?: string;
  subnet?: string;
  gateway?: string;
  dns?: string[];
  connectionType?: 'wired' | 'wireless';
  signalStrength?: number;
}

interface DeviceStatus {
  firmwareVersion?: string;
  hardwareVersion?: string;
  uptime?: number;
  temperature?: number;
  memoryUsage?: number;
  cpuUsage?: number;
  diskUsage?: number;
  lastReboot?: number;
}

interface StreamMetrics {
  bitrate?: number;
  packetLoss?: number;
  latency?: number;
  jitter?: number;
  frameRate?: number;
  resolution?: { width: number; height: number };
  codec?: string;
  lastMetricsUpdate?: number;
}

interface DeviceSecurity {
  authenticationMethods?: string[];
  encryptionSupport?: string[];
  certificateInfo?: {
    issuer?: string;
    subject?: string;
    validFrom?: number;
    validTo?: number;
  };
  accessLevel?: 'admin' | 'operator' | 'user';
}

interface DeviceInfo {
  urn: string;
  ipAddress: string;
  hostname: string;
  port: number;
  model: string;
  manufacturer?: string;
  serialNumber?: string;
  lastSeen: number;
  isOnline: boolean;
  rtspUrl?: string;
  
  // Enhanced device information
  capabilities?: DeviceCapabilities;
  networkInfo?: DeviceNetworkInfo;
  status?: DeviceStatus;
  streamMetrics?: StreamMetrics;
  security?: DeviceSecurity;
  
  // Discovery metadata
  discoveryMethod?: 'onvif' | 'manual' | 'imported';
  firstDiscovered?: number;
  lastVerified?: number;
  verificationFailures?: number;
}

interface VideoStreamProps {
  stream: MediaStream;
  producerId: string;
  streamName?: string;
  deviceInfo?: DeviceInfo;
}

const cardStyle: React.CSSProperties = {
  backgroundColor: '#333',
  borderRadius: '4px',
  padding: '1rem',
  margin: '0.5rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  border: '0.5px solid #444',
};

const getVideoStyle = (isFullscreen: boolean): React.CSSProperties => ({
  width: isFullscreen ? '100vw' : '100%',
  height: isFullscreen ? '100vh' : 'auto',
  maxWidth: isFullscreen ? '100vw' : '700px',
  borderRadius: '2px',
  backgroundColor: 'black',
  border: 'none',
  objectFit: isFullscreen ? 'contain' : 'cover',
});

const labelStyle: React.CSSProperties = {
  marginTop: '0.5rem',
  fontSize: '0.8rem',
  color: '#aaa',
  fontFamily: 'monospace',
};

const VideoStream: React.FC<VideoStreamProps> = ({ stream, producerId, streamName, deviceInfo }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [videoState, setVideoState] = useState<'loading' | 'playing' | 'error'>('loading');
  const [streamInfo, setStreamInfo] = useState<string>('');
  const [showFullInfo, setShowFullInfo] = useState(false);
  const [showController, setShowController] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Check if camera has PTZ capability
  const hasPTZ = deviceInfo?.capabilities?.panTiltZoom || 
                 (deviceInfo as any)?.profiles?.some((p: any) => p.PTZConfiguration);

  // Fullscreen handlers
  const toggleFullscreen = async () => {
    if (!containerRef.current) return;

    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (err) {
      console.error('Fullscreen error:', err);
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Debug: Log deviceInfo when it changes
  useEffect(() => {
    console.log(`[VideoStream ${producerId.substring(0,8)}] deviceInfo:`, deviceInfo);
    console.log(`[VideoStream ${producerId.substring(0,8)}] videoState: ${videoState}`);
  }, [deviceInfo, producerId, videoState]);

  useEffect(() => {
    if (videoRef.current && stream) {
      console.log(`[VideoStream ${producerId.substring(0,8)}] Attaching stream:`, stream);
      const video = videoRef.current;
      
      // Check if we're already using this stream
      if (video.srcObject === stream) {
        console.log(`[VideoStream ${producerId.substring(0,8)}] Stream already attached, skipping`);
        return;
      }
      
      // Set up event listeners for better feedback
      const handleLoadStart = () => {
        console.log(`[VideoStream ${producerId.substring(0,8)}] Load started`);
        setVideoState('loading');
        setStreamInfo('Loading video...');
      };

      const handleCanPlay = () => {
        console.log(`[VideoStream ${producerId.substring(0,8)}] Can play`);
        setStreamInfo('Video ready');
        // Try to play when ready
        video.play().catch(err => {
          console.error(`[VideoStream ${producerId.substring(0,8)}] Auto-play failed:`, err);
        });
      };

      const handlePlaying = () => {
        console.log(`[VideoStream ${producerId.substring(0,8)}] Playing`);
        setVideoState('playing');
        setStreamInfo('');
      };

      const handlePause = () => {
        console.log(`[VideoStream ${producerId.substring(0,8)}] Paused`);
        setVideoState('loading');
        setStreamInfo('Paused');
      };

      const handleEnded = () => {
        console.log(`[VideoStream ${producerId.substring(0,8)}] Ended`);
        setVideoState('loading');
        setStreamInfo('Stream ended');
      };

      const handleLoadedMetadata = () => {
        console.log(`[VideoStream ${producerId.substring(0,8)}] Metadata loaded`);
      };

      const handleLoadedData = () => {
        console.log(`[VideoStream ${producerId.substring(0,8)}] Data loaded`);
      };

      const handleError = (e: Event) => {
        console.error(`[VideoStream ${producerId.substring(0,8)}] Video error:`, e);
        setVideoState('error');
        setStreamInfo('Video error');
      };

      const handleWaiting = () => {
        console.log(`[VideoStream ${producerId.substring(0,8)}] Waiting for data`);
        setStreamInfo('Buffering...');
      };

      // Add event listeners
      video.addEventListener('loadstart', handleLoadStart);
      video.addEventListener('canplay', handleCanPlay);
      video.addEventListener('playing', handlePlaying);
      video.addEventListener('pause', handlePause);
      video.addEventListener('ended', handleEnded);
      video.addEventListener('error', handleError);
      video.addEventListener('waiting', handleWaiting);
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      video.addEventListener('loadeddata', handleLoadedData);

      // Debug stream tracks
      console.log(`[VideoStream ${producerId.substring(0,8)}] Stream tracks:`, stream.getTracks());
      console.log(`[VideoStream ${producerId.substring(0,8)}] Stream active:`, stream.active);
      
      // Set stream and configure video element
      video.srcObject = stream;
      
      // Optimize video element settings
      video.preload = 'auto';
      video.playsInline = true;
      video.muted = true;
      
      console.log(`[VideoStream ${producerId.substring(0,8)}] Video element configured`);
      
      // Add a small delay before playing to prevent interruption
      setTimeout(() => {
        if (video.srcObject === stream) {
          console.log(`[VideoStream ${producerId.substring(0,8)}] Attempting to play video`);
          video.play().catch(err => {
            console.error(`[VideoStream ${producerId.substring(0,8)}] Play failed:`, err);
            setVideoState('error');
            setStreamInfo('Play failed');
          });
        }
      }, 100);

      // Fallback: Check video state after a longer delay
      setTimeout(() => {
        if (video.srcObject === stream && video.readyState >= 2) {
          console.log(`[VideoStream ${producerId.substring(0,8)}] Fallback: Video ready, setting to playing`);
          setVideoState('playing');
          setStreamInfo('');
        }
      }, 1000);

      // Additional fallback: Check if video is actually playing
      setTimeout(() => {
        if (video.srcObject === stream && !video.paused && !video.ended && video.readyState >= 2) {
          console.log(`[VideoStream ${producerId.substring(0,8)}] Additional fallback: Video is playing, clearing overlay`);
          setVideoState('playing');
          setStreamInfo('');
        }
      }, 2000);

      // Enhanced periodic check: More comprehensive video playing detection
      const checkInterval = setInterval(() => {
        if (video.srcObject === stream) {
          const isActuallyPlaying = !video.paused && !video.ended && video.readyState >= 2;
          const hasTracks = stream.getTracks().length > 0;
          const tracksActive = stream.getTracks().every(track => track.readyState === 'live');

          if (isActuallyPlaying && hasTracks && tracksActive) {
            console.log(`[VideoStream ${producerId.substring(0,8)}] Enhanced check: Video is playing, clearing overlay`);
            setVideoState('playing');
            setStreamInfo('');
            clearInterval(checkInterval);
          }
        }
      }, 500);

      // Clean up interval after 10 seconds
      setTimeout(() => clearInterval(checkInterval), 10000);

      // Cleanup event listeners and interval
      return () => {
        clearInterval(checkInterval);
        video.removeEventListener('loadstart', handleLoadStart);
        video.removeEventListener('canplay', handleCanPlay);
        video.removeEventListener('playing', handlePlaying);
        video.removeEventListener('pause', handlePause);
        video.removeEventListener('ended', handleEnded);
        video.removeEventListener('error', handleError);
        video.removeEventListener('waiting', handleWaiting);
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        video.removeEventListener('loadeddata', handleLoadedData);
      };
    } else {
      console.warn(`[VideoStream ${producerId.substring(0,8)}] No stream or video element ref.`);
      setVideoState('error');
      setStreamInfo('No stream available');
    }
  }, [stream, producerId]);

  return (
    <div style={cardStyle}>
      <div 
        ref={containerRef}
        style={{ 
          position: 'relative', 
          width: '100%', 
          maxWidth: isFullscreen ? '100%' : '700px',
          height: isFullscreen ? '100vh' : 'auto',
          backgroundColor: 'black'
        }}
      >
        <video 
          ref={videoRef} 
          style={getVideoStyle(isFullscreen)} 
          autoPlay 
          playsInline 
          muted 
          preload="auto"
        />
        
        {/* Loading/Status Overlay - Only show when actually loading or has error, not when playing */}
        {((videoState === 'loading' || videoState === 'error') && stream) && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '1rem',
            borderRadius: '4px',
            fontSize: '0.9rem',
            textAlign: 'center',
            pointerEvents: 'none'
          }}>
            {videoState === 'loading' && (
              <div>
                <div style={{ marginBottom: '0.5rem' }}>🔄</div>
                <div>{streamInfo || 'Connecting to stream...'}</div>
              </div>
            )}
            {videoState === 'error' && (
              <div>
                <div style={{ marginBottom: '0.5rem' }}>⚠️</div>
                <div>{streamInfo}</div>
              </div>
            )}
          </div>
        )}

        {/* PTZ Controller */}
        {showController && hasPTZ && deviceInfo && (
          <CameraController
            cameraUrn={deviceInfo.urn}
            onClose={() => setShowController(false)}
          />
        )}

        {/* Bottom Overlay - Camera Info */}
        {deviceInfo && videoState === 'playing' && (
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 70%, transparent 100%)',
            padding: '1.5rem 1rem 0.75rem 1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            transition: 'all 0.3s ease',
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                marginBottom: '0.25rem'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: deviceInfo.isOnline ? '#10b981' : '#ef4444',
                  boxShadow: deviceInfo.isOnline ? '0 0 8px rgba(16, 185, 129, 0.6)' : '0 0 8px rgba(239, 68, 68, 0.6)',
                }} />
                <span style={{ 
                  color: 'white', 
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  textShadow: '0 1px 3px rgba(0,0,0,0.8)'
                }}>
                  {deviceInfo.model}
                </span>
              </div>
              <div style={{ 
                display: 'flex', 
                gap: '0.75rem',
                fontSize: '0.7rem',
                color: '#d1d5db',
                textShadow: '0 1px 2px rgba(0,0,0,0.8)'
              }}>
                <span>📍 {deviceInfo.ipAddress}</span>
                {deviceInfo.manufacturer && (
                  <span>🏭 {deviceInfo.manufacturer}</span>
                )}
                {deviceInfo.status?.firmwareVersion && (
                  <span>💾 v{deviceInfo.status.firmwareVersion.split('.')[0]}</span>
                )}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {/* Fullscreen Button */}
              <button
                onClick={toggleFullscreen}
                style={{
                  background: 'rgba(139, 92, 246, 0.2)',
                  border: '1px solid rgba(139, 92, 246, 0.4)',
                  color: 'white',
                  padding: '0.5rem',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                  width: '36px',
                  height: '36px',
                  backdropFilter: 'blur(8px)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(139, 92, 246, 0.4)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(139, 92, 246, 0.2)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
              >
                {isFullscreen ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3" />
                  </svg>
                )}
              </button>

              {/* PTZ Control Button */}
              {hasPTZ && (
                <button
                  onClick={() => setShowController(!showController)}
                  style={{
                    background: showController ? 'rgba(16, 185, 129, 0.3)' : 'rgba(16, 185, 129, 0.2)',
                    border: '1px solid rgba(16, 185, 129, 0.4)',
                    color: 'white',
                    padding: '0.5rem',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s',
                    width: '36px',
                    height: '36px',
                    backdropFilter: 'blur(8px)',
                    fontSize: '1rem',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(16, 185, 129, 0.4)';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = showController ? 'rgba(16, 185, 129, 0.3)' : 'rgba(16, 185, 129, 0.2)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title="PTZ Controls"
                >
                  🎯
                </button>
              )}

              {/* Info Button */}
              <button
                onClick={() => setShowFullInfo(!showFullInfo)}
                style={{
                  background: 'rgba(59, 130, 246, 0.2)',
                  border: '1px solid rgba(59, 130, 246, 0.4)',
                  color: 'white',
                  padding: '0.5rem',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                  width: '36px',
                  height: '36px',
                  backdropFilter: 'blur(8px)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(59, 130, 246, 0.4)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                title="Camera Info"
              >
                <svg 
                  width="18" 
                  height="18" 
                  viewBox="0 0 20 20" 
                  fill="currentColor"
                  style={{
                    transform: showFullInfo ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }}
                >
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Expanded Camera Information Panel */}
      {showFullInfo && deviceInfo && (
        <div style={{ marginTop: '1rem', width: '100%' }}>
          <CameraInfoCard 
            deviceInfo={deviceInfo} 
            className="w-full"
          />
        </div>
      )}
    </div>
  );
};

export default VideoStream;
